<template>
  <div class="flex h-full flex-col">
    <div class="flex flex-wrap items-center gap-2 pb-3 shrink-0">
      <FilterDropdown
        v-model="initiativeFilter"
        :options="initiativeOptions"
        :sensitive-labels="false"
        placeholder="All initiatives"
        clearable
      />
      <FilterDropdown
        v-model="gapFilter"
        :options="gapOptions"
        :sensitive-labels="false"
        placeholder="All gap types"
        clearable
      />
      <span v-if="themes.length" class="ml-auto text-xs text-gray-600">
        {{ visibleThemes.length }} of {{ themes.length }} themes · computed {{ formatDate(computedAt) }}
      </span>
    </div>

    <div class="min-h-0 overflow-y-auto" style="height: 500px">
      <div v-if="loading" class="divide-y divide-gray-100">
        <div v-for="n in 4" :key="n" class="px-4 py-4 animate-pulse space-y-2">
          <div class="h-3.5 bg-gray-100 rounded w-2/3"></div>
          <div class="h-2.5 bg-gray-100 rounded w-full"></div>
          <div class="h-2.5 bg-gray-100 rounded w-5/6"></div>
        </div>
      </div>

      <div v-else-if="!visibleThemes.length" class="py-10 text-center">
        <p class="text-sm text-gray-800">No themes yet</p>
        <p class="text-xs text-gray-600 mt-1">Themes are composed weekly from the specific constraints across every practice.</p>
      </div>

      <div v-else>
        <div
          v-for="(theme, idx) in visibleThemes"
          :key="idx"
          class="border-b border-gray-100 last:border-b-0 px-4 py-3"
        >
          <div class="flex items-start gap-3">
            <div class="flex-1 min-w-0">
              <p class="sensitive text-sm font-semibold leading-5 text-gray-900" data-demo-blur>{{ theme.title }}</p>
              <p class="sensitive mt-1 text-xs leading-4 text-gray-600" data-demo-blur>{{ theme.description }}</p>
              <p v-if="theme.recommended_play" class="sensitive mt-1 text-xs leading-4 text-gray-800" data-demo-blur>
                <span class="font-semibold">Play</span> {{ theme.recommended_play }}
              </p>
            </div>
            <div class="flex flex-col items-end gap-1 shrink-0">
              <div class="flex items-center gap-1">
                <Tag v-if="theme.initiative_key" :label="initiativeLabel(theme.initiative_key)" color="blue" size="sm" />
                <Tag v-if="theme.gap_type" :label="theme.gap_type" color="gray" size="sm" />
                <Tag v-if="theme.severity" :label="theme.severity" :color="severityColor(theme.severity)" size="sm" />
              </div>
              <span class="text-xs text-gray-600">{{ theme.practice_count }} practices · {{ theme.members.length }} constraints</span>
            </div>
          </div>
          <button
            type="button"
            class="mt-2 text-xs font-medium text-gray-600 hover:text-gray-900"
            @click="toggle(idx)"
          >
            {{ expanded[idx] ? 'Hide constraints' : 'Show constraints' }}
          </button>
          <ul v-if="expanded[idx]" class="mt-2 space-y-1">
            <li v-for="m in theme.members" :key="m.id" class="flex items-start gap-2 text-xs leading-4">
              <span class="h-1.5 w-1.5 mt-1.5 shrink-0 rounded-full" :class="m.status === 'in_progress' ? 'bg-neutral-500' : 'bg-red-dark'"></span>
              <span class="min-w-0">
                <router-link :to="{ path: `/entity/${m.practice_id}/engagement-tracker`, query: { node: m.id } }" class="sensitive font-semibold text-gray-800 hover:underline" data-demo-blur>{{ m.title }}</router-link>
                <span class="text-gray-600"> · <span class="sensitive">{{ m.practice }}</span><span v-if="m.gap_type"> · {{ m.gap_type }}</span></span>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue';
import Tag from '@/components/widgetcomponents/library/Tag.vue';
import { INITIATIVE_ORDER } from '@/components/EngagementTracker/engagementTrackerHelpers.js';

const GAP_TYPES = ['knowledge', 'capacity', 'ownership', 'motivation', 'patient_engagement', 'third_party'];

export default {
  name: 'ConstraintThemes',
  components: { FilterDropdown, Tag },
  props: {
    settings: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      initiativeFilter: null,
      gapFilter: null,
      themes: [],
      computedAt: null,
      loading: true,
      expanded: {},
    };
  },
  computed: {
    initiativeOptions() {
      const keys = [...new Set(this.themes.map(t => t.initiative_key).filter(Boolean))];
      keys.sort((a, b) => this.initiativeRank(a) - this.initiativeRank(b));
      return keys.map(k => ({ value: k, label: this.initiativeLabel(k) }));
    },
    gapOptions() {
      return GAP_TYPES.map(g => ({ value: g, label: g }));
    },
    visibleThemes() {
      return this.themes.filter(t =>
        (!this.initiativeFilter || t.initiative_key === this.initiativeFilter) &&
        (!this.gapFilter || t.gap_type === this.gapFilter)
      );
    },
  },
  async mounted() {
    const { data } = await axios.get('api/engagement-graph-nodes/themes/');
    this.themes = data.themes || [];
    this.computedAt = data.computed_at || null;
    this.loading = false;
  },
  methods: {
    toggle(id) { this.expanded = { ...this.expanded, [id]: !this.expanded[id] }; },
    severityColor(s) { return ({ high: 'red', medium: 'amber', low: 'gray' })[s] || 'gray'; },
    initiativeRank(key) {
      const i = INITIATIVE_ORDER.indexOf(key);
      return i === -1 ? INITIATIVE_ORDER.length : i;
    },
    initiativeLabel(key) {
      return key === 'unassigned' ? 'No initiative' : key.replaceAll('_', ' ');
    },
    formatDate(v) { return v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''; },
  },
};
</script>
